import { type Theme, type ThemeColor } from '@precis-digital/kurama/theme'
import { useTheme } from '@precis-digital/kurama/utils'
import { useMemo } from 'react'
import {
  createChartAvailableColorNamesMap,
  createChartAvailableColors,
  createChartSeriesColors,
  chartAvailableColorNames,
  createDefaults,
  createLegacyBadgeColorMap
} from '../constants'
import type { ChartCardDefaults, ChartColorName } from '../types'
import type { LegacyBadgeColor } from '../internalTypes'

export interface ChartCardConstants {
  defaults: ChartCardDefaults
  chartAvailableColorNamesMap: Record<ChartColorName, ThemeColor>
  chartAvailableColorNames: readonly ChartColorName[]
  chartAvailableColors: readonly ThemeColor[]
  /** Distinct `*50` hues for auto-assigning colours to series / dimension values. */
  chartSeriesColors: readonly ThemeColor[]
  legacyBadgeColorMap: Record<LegacyBadgeColor, ThemeColor>
}

export const getChartCardConstants = (theme: Theme): ChartCardConstants => {
  const chartAvailableColorNamesMap = createChartAvailableColorNamesMap(theme)
  const chartAvailableColors = createChartAvailableColors(chartAvailableColorNamesMap)
  const chartSeriesColors = createChartSeriesColors(chartAvailableColorNamesMap)

  return {
    defaults: createDefaults(theme, chartAvailableColors),
    chartAvailableColorNamesMap,
    chartAvailableColorNames,
    chartAvailableColors,
    chartSeriesColors,
    legacyBadgeColorMap: createLegacyBadgeColorMap(theme)
  }
}

export const useChartCardConstants = (): ChartCardConstants => {
  const theme = useTheme()

  return useMemo(() => getChartCardConstants(theme), [theme])
}
